import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { useMount } from 'react-use';
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilValueLoadable } from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useJsonToCsv } from 'react-json-csv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import {
  Button,
  Table,
  notification,
  Avatar,
  Tooltip,
  Modal,
  DatePicker,
  Input,
} from 'antd';
import { Comment } from '@ant-design/compatible';
import {
  CloseCircleOutlined,
  FormOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { PageHeader } from '@ant-design/pro-layout';
import {
  timezoneAtom,
  profileAtom,
  giftCardHistoryAtom,
  selectedOrderAtom,
  viewDetailsAtom,
  setShouldReloadGiftCardsAtom
} from '../../../../atoms/Atoms';
import {
  formatPhoneNumber,
  toCurrency,
  mapOrderType,
  mapOrderStatus,
  renderShortDateTime,
  renderDate,
  mapPaymentType,
  renderShortDateHour,
  mapOrderForDetailsModal
} from '../../../utils/functions';
import api from '../../../../api/api';
import { dateRangePresets } from '../../../utils/utils';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';

function GiftCardsHistoryModal() {
  const bottomRowGridStyle = { width: '100%', textAlign: 'center' };
  const { RangePicker } = DatePicker;
  const { t } = useTranslation();
  const { saveAsCsv } = useJsonToCsv();
  const timezone = useRecoilValue(timezoneAtom);
  const profile = useRecoilValueLoadable(profileAtom);
  const [selectedGiftCard, setSelectedGiftCard] = useRecoilState(giftCardHistoryAtom);
  const setShouldReloadGiftCards = useSetRecoilState(setShouldReloadGiftCardsAtom);
  const [selectedOrder, setSelectedOrder] = useRecoilState(selectedOrderAtom);
  const setViewDetails = useSetRecoilState(viewDetailsAtom);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSize, setCurrentSize] = useState(20);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [editNickname, seteditNickname] = useState(false);
  const [cardNickname, setCardNickname] = useState('');

  const isSuperAdmin =
    profile.contents && profile?.contents?.roles?.includes('SuperAdmin');
  const isAdmin = isSuperAdmin ||
    (profile.contents && profile?.contents?.roles?.includes('CustomerSupportAdmin'));

  const isStoreManager =
    isAdmin ||
    (profile?.contents &&
      (profile?.contents?.roles?.includes('StoreAdmin') ||
        profile?.contents?.roles?.includes('Owner')));
  const canViewFinance =
    isAdmin ||
    isStoreManager ||
    (profile.contents &&
      (profile.contents.roles.includes('Finance') ||
        profile.contents.roles.includes('TopAnalytics')));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  function getTransactionType(type) {
    if (type === 1) {
      return t('redemption');
    }
    return t('load');
  }

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getData(page) {
    setLoading(true);
    const data = {
      Page: page,
      Size: currentSize
    };
    api
      .post(
        `giftcards/history/${selectedGiftCard?.id}`,
        data
      )
      .then((response) => {
        setLoading(false);
        setReportData(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }

  function recalculateBalance() {
    setLoading(true);
    api.post(`giftcards/recalc/balance/${selectedGiftCard?.id}`, null)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSelectedGiftCard(response.data.data);
          getData(currentPage);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        showMessage(error.message);
        setLoading(false);
      });
  }

  function loadOrder(orderId) {
    setLoadingOrder(true);
    let tempReportData = JSON.parse(JSON.stringify(reportData));
    for (let i = 0; i < tempReportData.transactions.length; i++) {
      const transaction = tempReportData.transactions[i];
      if (transaction.orderId === orderId) {
        transaction.loading = true;
      }
    }
    setReportData(tempReportData);
    api
      .get(`orders/order/${orderId}`)
      .then((response) => {
        setLoadingOrder(false);
        const order = response.data.data;
        tempReportData = JSON.parse(JSON.stringify(reportData));
        for (let i = 0; i < tempReportData.transactions.length; i++) {
          const transaction = tempReportData.transactions[i];
          transaction.loading = false;
        }
        setReportData(tempReportData);
        setViewDetails(true);
        const tempOrder = mapOrderForDetailsModal({
          ...order,
          dontHideModal: true
        }, timezone, t);
        setSelectedOrder(tempOrder);
      })
      .catch((error) => {
        console.error(error);
        setLoadingOrder(false);
        for (let i = 0; i < tempReportData.transactions.length; i++) {
          const transaction = tempReportData.transactions[i];
          transaction.loading = false;
        }
      });
  }

  function revert(id) {
    setLoadingOrder(true);
    let tempReportData = JSON.parse(JSON.stringify(reportData));
    for (let i = 0; i < tempReportData.transactions.length; i++) {
      const transaction = tempReportData.transactions[i];
      if (transaction.id === id) {
        transaction.loading = true;
      }
    }
    setReportData(tempReportData);
    api.post(`giftcards/revert/${id}`)
      .then((response) => {
        if (response.data.success) {
          setLoadingOrder(false);
          tempReportData = JSON.parse(JSON.stringify(reportData));
          for (let i = 0; i < tempReportData.transactions.length; i++) {
            const transaction = tempReportData.transactions[i];
            transaction.loading = false;
          }
          getData(currentPage);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
        setLoadingOrder(false);
        for (let i = 0; i < tempReportData.transactions.length; i++) {
          const transaction = tempReportData.transactions[i];
          transaction.loading = false;
        }
      });
  }

  function updateNickName() {
    const data = {
      NewNickname: cardNickname,
    };
    api
      .post(
        `giftcards/nickname/${selectedGiftCard?.id}`,
        data
      )
      .then((response) => {
        if (response.data.success && !selectedGiftCard?.dontHideModal) {
          setShouldReloadGiftCards(true);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  const [width, setWidth] = useState(window.innerWidth);
  const allColumns = [
    {
      title: '',
      key: 'mobile',
      align: 'center',
      className: 'text-xs md:hidden',
      render: (row) => (
        <div className="border border-1 p-2 grid gap-2 grid-cols-2">
          <div className="text-left">
            <p className="font-bold">{t('day')}</p>
            <p>{renderShortDateTime(row.createdAt, timezone)}</p>
          </div>
          <div className="text-right">
            <p className="font-bold">{t('transaction')}</p>
            <p>{getTransactionType(row.transactionType)}</p>
          </div>

          <div className="text-left">
            <p className="font-bold">{t('amount')}</p>
            <p>{toCurrency(row.amount)}</p>
          </div>
          <div className="text-right">
            <p className="font-bold">{t('final_balance')}</p>
            <p>{toCurrency(row.giftCardCurrentBalance)}</p>
          </div>

          <div className="text-left">
            {row.orderId && (
              <Button
                size="small"
                type="primary"
                loading={row.loading}
                disabled={loadingOrder || selectedGiftCard?.dontHideModal}
                onClick={() => loadOrder(row.orderId)}
              >
                {row.orderConfirmationCode}
              </Button>
            )}
          </div>
          <div className="text-right">
            <p className="font-bold">{t('business')}</p>
            <p>{row.orderBusinessName}</p>
          </div>

          <div className="text-center col-span-2">
            {isAdmin && (
              <Button
                size="small"
                type="primary"
                loading={row.loading}
                disabled={loadingOrder}
                onClick={() => revert(row.id)}
              >
                {t('revert')}
              </Button>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('day'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{renderShortDateTime(text, timezone)}</span>,
    },
    {
      title: t('transaction'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{getTransactionType(text)}</span>,
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{toCurrency(text)}</span>,
    },
    {
      title: t('final_balance'),
      dataIndex: 'giftCardCurrentBalance',
      key: 'giftCardCurrentBalance',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{toCurrency(text)}</span>,
    },
    {
      title: t('order'),
      key: 'orderConfirmationCode',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (row) => (
        <>
          {row.orderId && (
            <Button
              size="small"
              type="primary"
              loading={row.loading}
              disabled={loadingOrder || selectedGiftCard?.dontHideModal}
              onClick={() => loadOrder(row.orderId)}
            >
              {row.orderConfirmationCode}
            </Button>
          )}
          {!row.orderId && (
            <span>-</span>
          )}
        </>
      ),
    },
    {
      title: t('business'),
      key: 'orderBusinessName',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (row) => (
        <>
          {row.orderId && (
            <span>{row.orderBusinessName}</span>
          )}
          {!row.orderId && (
            <span>-</span>
          )}
        </>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      className: `${isAdmin ? '' : 'hidden'} text-xs hidden md:table-cell`,
      render: (row) => (
        <>
          <Button
            size="small"
            type="primary"
            loading={row.loading}
            disabled={loadingOrder}
            onClick={() => revert(row.id)}
          >
            {t('revert')}
          </Button>
        </>
      ),
    },
  ];

  useEffect(() => {
    if (selectedGiftCard) {
      setCurrentPage(0);
      getData(0);
    } else {
      setReportData(null);
    }
  }, [currentSize, selectedGiftCard]);

  useEffect(() => {
    setWidth(window.innerWidth);
  }, [window.innerWidth]);

  useEffect(() => {
    setViewDetails(false);
  }, []);

  useEffect(() => {
    if (!selectedGiftCard) {
      seteditNickname(false);
    } else {
      setCardNickname(selectedGiftCard?.nickname);
    }
  }, [selectedGiftCard]);

  return (
    <>
      <Modal
        width="80%"
        title={`${t('gift_card')} - ${selectedGiftCard?.code}`}
        open={selectedGiftCard && (selectedGiftCard?.dontHideModal || (!selectedGiftCard?.dontHideModal && !selectedOrder))}
        onCancel={() => {
          setSelectedGiftCard(null);
        }}
        footer={[
          <Button
            key="recalculate"
            type="primary"
            loading={loading}
            hidden={!isSuperAdmin}
            onClick={() => recalculateBalance()}
          >
            {t('recalc_balance')}
          </Button>,
          <Button
            key="close"
            type="primary"
            danger
            loading={loading}
            onClick={() => {
              setSelectedGiftCard(null);
            }}
          >
            {t('close')}
          </Button>
        ]}
      >
        <div>
          {!editNickname && (
            <div className="space-x-2">
              <span className={cardNickname?.length > 0 ? '' : 'italic text-gray-600'}>{cardNickname?.length > 0 ? cardNickname : t('no_nickname')}</span>
              <FormOutlined
                onClick={() => { seteditNickname(true); }}
              />
            </div>
          )}
          {editNickname && (
            <div className="flex space-x-2">
              <Input
                value={cardNickname}
                onChange={(obj) => setCardNickname(obj.target.value)}
              />
              <CloseCircleOutlined
                onClick={() => {
                  updateNickName();
                  seteditNickname(false);
                }}
              />
            </div>
          )}
        </div>
        <PageHeader
          className="mb-4 mt-4 px-0"
          title={t('history')}
          extra={
            [
              <div className="space-x-2">
                <br className={width < 1024 ? null : 'hidden'} />
                <Button
                  type="primary"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setCurrentPage(0);
                    getData(0);
                  }}
                >
                  {t('refresh')}
                </Button>
              </div>
            ]
          }
        />
        <Table
          loading={loading}
          bordered
          pagination={{
            pageSize: currentSize,
            showSizeChanger: true,
            defaultCurrent: 0,
            current: currentPage + 1,
            total: reportData?.total,
            onChange: (page, pageSize) => {
              if (page - 1 !== currentPage) {
                setCurrentPage(page - 1);
                getData(page - 1);
              } else {
                setCurrentSize(pageSize);
              }
            }
          }}
          columns={allColumns}
          dataSource={reportData?.transactions}
        />
      </Modal>
      {/* <OrderDetailsModal clearOrderOnClose /> */}
    </>
  );
}

export default GiftCardsHistoryModal;
